import { useState } from "react";
import { MessageCircle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { api, WHATSAPP_URL } from "@/lib/api";

const FAQ = [
  {
    q: "Quels sont les délais de livraison ?",
    a: "2 à 4 jours ouvrés partout au Canada, 5 à 8 jours pour l'international. Livraison offerte dès 150€ d'achat.",
  },
  {
    q: "Comment choisir ma taille ?",
    a: "Nos hoodies et tees taillent oversize : prends ta taille habituelle pour le look street, une taille en dessous pour un fit plus près du corps. Les vestes shell taillent normalement.",
  },
  {
    q: "Quelle est la politique de retour ?",
    a: "30 jours pour changer d'avis, article non porté avec étiquettes. Échange de taille gratuit, remboursement sous 5 jours ouvrés après réception.",
  },
  {
    q: "Les vestes sont-elles vraiment étanches ?",
    a: "Oui : membrane 20K/20K (20 000mm d'étanchéité, 20 000g de respirabilité), coutures thermosoudées et zips YKK Aquaguard. Testées en storm riding dans l'arrière-pays québécois.",
  },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message envoyé. On te répond vite.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Envoi impossible. Réessaie ou écris-nous sur WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-12 pt-32 pb-24" data-testid="contact-page">
      <Seo
        title="Contact — S'NO"
        siteName="S'NO"
        description="Une question sur une pièce, une taille ou une commande ? Écris-nous directement sur WhatsApp ou via le formulaire."
        image="/logo.webp"
      />

      <p className="font-mono text-xs uppercase tracking-[0.4em] text-ice mb-3">SAV / Questions</p>
      <h1 className="font-display font-black uppercase text-5xl sm:text-6xl lg:text-7xl leading-none mb-14">
        Contact
      </h1>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        {/* WhatsApp card */}
        <div>
          <div className="border border-white/10 bg-smoke p-8 sm:p-10" data-testid="whatsapp-contact-card">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
              Réponse la plus rapide
            </p>
            <h2 className="font-display font-black uppercase text-4xl leading-none mb-4">
              Écris-nous sur <span className="text-[#25D366]">WhatsApp</span>
            </h2>
            <p className="text-sm text-zinc-400 leading-relaxed mb-8">
              Question sur une taille, un drop, un suivi de commande ou du SAV ?
              On répond en direct, 7j/7.
            </p>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-button"
              className="inline-flex items-center gap-3 bg-[#25D366] text-black font-display font-bold uppercase tracking-widest text-lg px-8 py-4 shadow-[0_0_25px_rgba(37,211,102,0.35)] hover:scale-[1.02] hover:shadow-[0_0_40px_rgba(37,211,102,0.55)] transition-[transform,box-shadow] duration-300"
            >
              <MessageCircle className="w-5 h-5" /> +1 514 826 0610
            </a>
          </div>

          {/* FAQ */}
          <div className="mt-10" data-testid="faq-section">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-4">FAQ</p>
            <Accordion type="single" collapsible className="border-t border-white/10">
              {FAQ.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="font-display uppercase tracking-wide text-lg text-left text-white hover:text-ice hover:no-underline"
                  >
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-zinc-400 leading-relaxed">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>

        {/* Form */}
        <div className="border border-white/10 bg-smoke p-8 sm:p-10 h-fit">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
            Formulaire
          </p>
          <h2 className="font-display font-black uppercase text-3xl leading-none mb-8">
            Laisse un message
          </h2>
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <input
              type="text"
              required
              maxLength={200}
              placeholder="Ton nom"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="contact-name-input"
              className="w-full bg-black border border-white/15 px-5 py-4 font-mono text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-ice transition-colors"
            />
            <input
              type="email"
              required
              maxLength={200}
              placeholder="ton@email.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="contact-email-input"
              className="w-full bg-black border border-white/15 px-5 py-4 font-mono text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-ice transition-colors"
            />
            <textarea
              required
              maxLength={5000}
              rows={6}
              placeholder="Ton message…"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="contact-message-input"
              className="w-full bg-black border border-white/15 px-5 py-4 font-mono text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-ice transition-colors resize-none"
            />
            <button type="submit" disabled={sending} className="btn-ice w-full disabled:opacity-60" data-testid="contact-submit-button">
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-4 h-4" />}
              Envoyer
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
